import { getJobDurationLabel } from "@/common/enums/JobDuration";
import { createFileRoute } from "@tanstack/react-router";
import { type ReactNode, useCallback, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  InfiniteData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CitySelect } from "@/components/Selects/CitySelect";
import type { CityOption } from "@/components/Selects/CitySelect";
import { SpecialtiesSelect } from "@/components/Selects/SpecialtiesSelect";
import { USStateSelect } from "@/components/Selects/USStateSelect";
import { HealthcareProfessionalTypeSelect } from "@/components/Selects/HealthcareProfessionalTypeSelect";
import { EmploymentTypeSelect } from "@/components/Selects/EmploymentTypeSelect";
import { RouterOutputs, trpc, useTRPCClient } from "@/trpc";
import { EmploymentType } from "@api/shared-runtime/enums";
import { toast } from "sonner";
import {
  ArrowLeft,
  Building2,
  Calendar,
  CheckCircle2,
  ChevronDown,
  FileSignature,
  Clock,
  Eye,
  Info,
  LayoutDashboard,
  Loader2,
  MapPin,
  MessageCircle,
  Pencil,
  RefreshCw,
  User,
  Undo2,
  UserMinus,
  UserPlus,
  Users,
  StopCircle,
  Database,
} from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@radix-ui/react-popover";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { ChatThreadBody } from "@/features/chat/chat-thread-body";
import type { ChatThreadMessage } from "@/features/chat/types/chat-thread-message";
import { adaptAdminMessageToUserMessage } from "@/features/chat/admin/adaptAdminMessageToUserMessage";
import { ProfileModal } from "@/features/profile/components/ProfileModal";
import { AdminUserDetails } from "@/features/user/components/AdminUserDetails";
import { format, formatDistanceToNow } from "date-fns";
import { Textarea } from "@/components/ui/textarea";
import { RenderHtml } from "@/components/RenderHtml";
import { JobCard } from "@/features/job-post/job-card";
import type { JobPost } from "@/features/job-post/types/job-post";

const searchFormSchema = z.object({
  specialties: z.array(z.string()).default([]),
  professionalTypes: z
    .array(
      z.object({
        id: z.string(),
        name: z.string(),
      }),
    )
    .default([]),
  city: z.array(
    z.object({
      name: z.string(),
      lat: z.number().optional(),
      lng: z.number().optional(),
    }),
  ),
  distance: z.string().min(1, "Please select a distance"),
  employmentTypeIds: z.array(z.nativeEnum(EmploymentType)).optional(),
  stateLicenses: z.array(z.string()),
  hasImlcLicense: z.string(),
});

type SearchFormData = z.infer<typeof searchFormSchema>;

type CandidateStatusFilter = "matches" | "rejected" | "all";

type ProviderCandidateStatus =
  RouterOutputs["jobPost"]["adminGetPotentialCandidateStatuses"][number];

type PotentialCandidate =
  RouterOutputs["jobPost"]["adminGetPotentialCandidates"]["items"][number];

type PotentialCandidatePage =
  RouterOutputs["jobPost"]["adminGetPotentialCandidates"];
type AdminJobPost = NonNullable<RouterOutputs["jobPost"]["adminGetById"]>;
type InterestedCandidate = NonNullable<
  NonNullable<AdminJobPost["interestedCandidates"]>[number]
>;
type ParsedProfessionalType = { id: string; name: string };
type CandidateSpecialty = NonNullable<
  NonNullable<PotentialCandidate["specialties"]>[number]
>;
type StateMedicalLicense = {
  state: string;
};

type OverviewItem = {
  key: string;
  label: string;
  value: ReactNode;
};

const distanceOptions = [
  { value: "50", label: "50 miles" },
  { value: "100", label: "100 miles" },
  { value: "300", label: "300 miles" },
];

const candidateQueryKey = (jobPostId: string, status: CandidateStatusFilter) =>
  ["jobPost.adminGetPotentialCandidates", jobPostId, status] as const;

const candidateStatusesQueryKey = (jobPostId: string, providerIdsKey: string) =>
  ["jobPost.adminCandidateStatuses", jobPostId, providerIdsKey] as const;
const isStateMedicalLicense = (
  license: unknown,
): license is StateMedicalLicense =>
  typeof license === "object" &&
  license !== null &&
  "state" in license &&
  typeof license.state === "string" &&
  license.state.length > 0;

const candidateStatusFilters: CandidateStatusFilter[] = ["matches", "rejected"];
const MAX_CHAT_PREVIEW_LENGTH = 140;

const getLastChatPreview = (candidate: PotentialCandidate) => {
  if (!candidate.chatMessage) {
    return "No chat history yet";
  }

  const sanitize = (value: string) => value.replace(/\s+/g, " ").trim();
  const ellipsize = (value: string) =>
    value.length > MAX_CHAT_PREVIEW_LENGTH
      ? `${value.slice(0, MAX_CHAT_PREVIEW_LENGTH - 1)}…`
      : value;

  if (candidate.chatMessage?.text) {
    const preview = candidate.chatMessage?.text || "Message sent";
    return ellipsize(sanitize(preview));
  }

  return "Latest activity: Job offer shared";
};

const getStatusBadge = (job: AdminJobPost | null | undefined) => {
  if (!job) {
    return null;
  }

  if (!job.publishedAt) {
    return (
      <Badge className="bg-gray-100 text-gray-700 border-gray-300">
        <FileSignature className="h-3 w-3 mr-1" />
        Draft
      </Badge>
    );
  }

  if (!job.reviewedAt) {
    return (
      <Badge className="bg-yellow-100 text-yellow-700 border-yellow-300">
        <Clock className="h-3 w-3 mr-1" />
        Pending Review
      </Badge>
    );
  }

  return (
    <Badge className="bg-green-100 text-green-700 border-green-300">
      <CheckCircle2 className="h-3 w-3 mr-1" />
      Approved
    </Badge>
  );
};

const PotentialCandidatesPage = () => {
  const { jobPostId } = Route.useParams();
  const navigate = Route.useNavigate();
  const queryClient = useQueryClient();
  const trpcClient = useTRPCClient();
  const search = Route.useSearch();
  const [candidateStatusFilter, setCandidateStatusFilter] =
    useState<CandidateStatusFilter>("matches");

  const jobQuery = useQuery(
    trpc.jobPost.adminGetById.queryOptions({ jobPostId }),
  );
  const job = jobQuery.data;
  const durationText = (job?.durationIds ?? [])
    .map(getJobDurationLabel)
    .filter(Boolean)
    .join(" • ");
  const professionalTypes = job?.healthcareProfessionalTypes ?? [];
  const specialties = job?.specialties ?? [];
  const organizationBusinessUnitWithOrg = job?.organizationBusinessUnit as
    | (typeof job extends { organizationBusinessUnit?: infer O } ? O : never)
    | (Record<string, unknown> & { organization?: { name?: string } })
    | undefined;
  const jobOrganization = (job as Record<string, unknown> | undefined)
    ?.organization as { name?: string } | undefined;
  const organizationName =
    organizationBusinessUnitWithOrg?.organization?.name ??
    jobOrganization?.name ??
    job?.organizationBusinessUnit?.name ??
    "Unknown organization";
  const businessUnitName = job?.organizationBusinessUnit?.name ?? null;

  const candidatesQuery = useInfiniteQuery<
    PotentialCandidatePage,
    Error,
    InfiniteData<PotentialCandidatePage>,
    ReturnType<typeof candidateQueryKey>,
    string | undefined
  >({
    queryKey: candidateQueryKey(jobPostId, candidateStatusFilter),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    queryFn: async ({ pageParam }) =>
      trpcClient.jobPost.adminGetPotentialCandidates.query({
        jobPostId,
        status: candidateStatusFilter,
        cursor: pageParam ?? undefined,
      }),
  });

  const candidatePages = candidatesQuery.data?.pages ?? [];
  const candidates: PotentialCandidate[] = candidatePages.flatMap(
    (page) => page.items,
  );
  const candidateCounts =
    candidatePages.find((page) => page.counts)?.counts ?? null;
  const matchesCount = candidateCounts?.matches ?? 0;
  const rejectedCount = candidateCounts?.rejected ?? 0;
  const isViewingRejected = candidateStatusFilter === "rejected";
  const invalidateCandidateLists = async () => {
    await Promise.all(
      candidateStatusFilters.map((status) =>
        queryClient.invalidateQueries({
          queryKey: candidateQueryKey(jobPostId, status),
        }),
      ),
    );
  };
  const invalidateJobSummaries = async () => {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: trpc.jobPost.adminGetById.queryKey({ jobPostId }),
      }),
      queryClient.invalidateQueries({
        queryKey: trpc.jobPost.adminGetAll.queryKey(),
      }),
    ]);
  };
  const invalidateCandidateStatuses = async () => {
    await queryClient.invalidateQueries({
      queryKey: ["jobPost.adminCandidateStatuses", jobPostId],
      exact: false,
    });
  };
  const reviewMutation = useMutation(
    trpc.jobPost.approve.mutationOptions({
      onSuccess: async () => {
        await invalidateJobSummaries();
        setReviewDialogOpen(false);
        toast.success("Job post approved successfully");
      },
      onError: (error) => {
        toast.error("Failed to approve job post: " + error.message);
      },
    }),
  );

  const removeApprovalMutation = useMutation(
    trpc.jobPost.removeApproval.mutationOptions({
      onSuccess: async () => {
        await invalidateJobSummaries();
        setUnapproveDialogOpen(false);
        toast.success("Job post approval removed successfully");
      },
      onError: (error) => {
        toast.error("Failed to remove approval: " + error.message);
      },
    }),
  );

  const reevaluateCandidatesMutation = useMutation(
    trpc.jobPost.adminReevaluateCandidates.mutationOptions({
      onSuccess: async () => {
        toast.success(
          "Candidate reevaluation started. Results will appear shortly.",
        );
      },
      onError: (error) => {
        toast.error("Failed to reevaluate candidates: " + error.message);
      },
    }),
  );

  const handleApproveJob = () => {
    if (!job) {
      return;
    }

    reviewMutation.mutate({ id: job.id });
  };

  const handleRemoveApproval = () => {
    if (!job) {
      return;
    }

    removeApprovalMutation.mutate({ id: job.id });
  };

  const [chatDialogUser, setChatDialogUser] = useState<{
    id: string;
    firstName: string | null;
    lastName: string | null;
  } | null>(null);
  const [chatDialogThreadId, setChatDialogThreadId] = useState<string | null>(
    null,
  );
  const [isChatDialogOpen, setIsChatDialogOpen] = useState(false);
  const [chatLoadingUserId, setChatLoadingUserId] = useState<string | null>(
    null,
  );
  const [profileModalUserId, setProfileModalUserId] = useState<string | null>(
    null,
  );
  const [userDetailsUserId, setUserDetailsUserId] = useState<string | null>(
    null,
  );
  const [candidateDialogState, setCandidateDialogState] = useState<{
    candidate: PotentialCandidate;
    mode: "reject" | "reinstate";
  } | null>(null);
  const [candidateReason, setCandidateReason] = useState("");
  const candidateSectionRef = useRef<HTMLDivElement | null>(null);
  const scrollToCandidates = useCallback(() => {
    candidateSectionRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);
  const [previewJob, setPreviewJob] = useState<JobPost | null>(null);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [unapproveDialogOpen, setUnapproveDialogOpen] = useState(false);
  const [detailsExpanded, setDetailsExpanded] = useState(false);
  const [interestedExpanded, setInterestedExpanded] = useState(false);
  const [sqlDialogOpen, setSqlDialogOpen] = useState(false);
  const formatCurrency = (value?: number | null, minimumFractionDigits = 0) => {
    if (typeof value !== "number" || Number.isNaN(value)) {
      return null;
    }
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits,
      maximumFractionDigits: value % 1 === 0 ? 0 : 2,
    }).format(value);
  };

  const candidateDialogName = candidateDialogState
    ? [
        candidateDialogState.candidate.firstName,
        candidateDialogState.candidate.lastName,
      ]
        .filter((part) => part && part.length > 0)
        .join(" ")
        .trim() || "this provider"
    : "";
  const isReinstatingCandidate = candidateDialogState?.mode === "reinstate";

  const ensureChatMutation = useMutation(
    trpc.jobPost.adminEnsurePotentialCandidateChat.mutationOptions({
      onError: (error) => {
        toast.error(
          error?.message ??
            "Unable to open chat. Please check configuration and try again.",
        );
      },
    }),
  );

  const mockOnInterestChange = async (
    previewJobId: string,
    isInterested: boolean | null,
  ) => {
    console.log("Preview mode - interest change would be:", {
      jobId: previewJobId,
      isInterested,
    });
  };

  const parsedSpecialties =
    search.specialties?.split(",").filter(Boolean) ??
    (search.specialty ? [search.specialty] : []);

  let parsedProfessionalTypes: { id: string; name: string }[] = [];
  if (search.professionalTypes) {
    try {
      const values = JSON.parse(search.professionalTypes);
      if (Array.isArray(values)) {
        parsedProfessionalTypes = values.filter(
          (value: unknown): value is ParsedProfessionalType =>
            typeof value === "object" &&
            value !== null &&
            "id" in value &&
            "name" in value &&
            typeof value.id === "string" &&
            typeof value.name === "string",
        );
      }
    } catch {
      parsedProfessionalTypes = [];
    }
  } else if (search.professionalTypeId) {
    parsedProfessionalTypes = [
      {
        id: search.professionalTypeId,
        name: search.professionalTypeName || "",
      },
    ];
  }

  const {
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<SearchFormData>({
    resolver: zodResolver(searchFormSchema),
    defaultValues: {
      specialties: parsedSpecialties,
      professionalTypes: parsedProfessionalTypes,
      city: search.cityName
        ? [
            {
              name: search.cityName || "",
              lat: Number(search.lat) || undefined,
              lng: Number(search.lng) || undefined,
            },
          ]
        : [],
      distance: search.distance || "100",
      employmentTypeIds: search.employmentTypeIds
        ? (search.employmentTypeIds.split(",") as EmploymentType[])
        : undefined,
      stateLicenses: search.stateLicenses
        ? search.stateLicenses.split(",")
        : [],
      hasImlcLicense: search.hasImlcLicense || "any",
    },
  });

  const [searchParams, setSearchParams] = useState<{
    lat?: number;
    lng?: number;
    specialtyCodes?: string[];
    distanceMiles?: number;
    employmentTypeIds?: EmploymentType[];
    stateLicenses?: string[];
    hasImlcLicense?: boolean;
    healthcareProfessionalTypeIds?: string[];
  } | null>(() => {
    const latValue = search.lat ? Number(search.lat) : undefined;
    const lngValue = search.lng ? Number(search.lng) : undefined;
    const distanceMiles = search.distance ? Number(search.distance) : undefined;
    const specialtyCodes =
      parsedSpecialties && parsedSpecialties.length > 0
        ? parsedSpecialties
        : undefined;
    const employmentTypeIds = search.employmentTypeIds
      ? (search.employmentTypeIds.split(",") as EmploymentType[])
      : undefined;
    const parsedStateLicenses = search.stateLicenses
      ? search.stateLicenses.split(",").filter(Boolean)
      : undefined;
    const stateLicenses =
      parsedStateLicenses && parsedStateLicenses.length > 0
        ? parsedStateLicenses
        : undefined;
    const hasImlcLicense =
      search.hasImlcLicense === "true"
        ? true
        : search.hasImlcLicense === "false"
          ? false
          : undefined;
    const healthcareProfessionalTypeIds =
      parsedProfessionalTypes.length > 0
        ? parsedProfessionalTypes.map((type) => type.id)
        : undefined;

    const hasFilters =
      (specialtyCodes && specialtyCodes.length > 0) ||
      (latValue !== undefined && lngValue !== undefined) ||
      (stateLicenses && stateLicenses.length > 0) ||
      typeof hasImlcLicense === "boolean" ||
      (healthcareProfessionalTypeIds &&
        healthcareProfessionalTypeIds.length > 0);

    if (!hasFilters) {
      return null;
    }

    return {
      lat: latValue,
      lng: lngValue,
      specialtyCodes,
      distanceMiles,
      employmentTypeIds,
      stateLicenses,
      hasImlcLicense,
      healthcareProfessionalTypeIds,
    };
  });

  const hasSearchFilters =
    !!searchParams &&
    ((searchParams.specialtyCodes && searchParams.specialtyCodes.length > 0) ||
      (searchParams.stateLicenses && searchParams.stateLicenses.length > 0) ||
      typeof searchParams.hasImlcLicense === "boolean" ||
      (searchParams.lat !== undefined && searchParams.lng !== undefined) ||
      (searchParams.healthcareProfessionalTypeIds &&
        searchParams.healthcareProfessionalTypeIds.length > 0));

  const providersQuery = useQuery({
    ...trpc.organization.adminSearchProviders.queryOptions(searchParams ?? {}),
    enabled: hasSearchFilters,
  });

  const addMutation = useMutation(
    trpc.jobPost.adminAddPotentialCandidate.mutationOptions({
      onSuccess: async () => {
        toast.success("Candidate added to potential list");
        await Promise.all([
          invalidateCandidateLists(),
          invalidateJobSummaries(),
          invalidateCandidateStatuses(),
        ]);
      },
      onError: (error) => {
        toast.error(error.message || "Unable to add candidate");
      },
    }),
  );

  const closeCandidateDialog = () => {
    setCandidateDialogState(null);
    setCandidateReason("");
  };

  const updateCandidateStatusMutation = useMutation(
    trpc.jobPost.adminUpdatePotentialCandidateStatus.mutationOptions({
      onSuccess: async (_, variables) => {
        toast.success(
          variables.isMatch
            ? "Candidate added back to matches"
            : "Candidate marked as rejected",
        );
        await Promise.all([
          invalidateCandidateLists(),
          invalidateJobSummaries(),
          invalidateCandidateStatuses(),
        ]);
        closeCandidateDialog();
      },
      onError: (error) => {
        toast.error(error.message || "Unable to update candidate");
      },
    }),
  );

  const handleConfirmCandidateDialog = () => {
    if (!candidateDialogState) {
      return;
    }
    const trimmedReason = candidateReason.trim();
    if (!trimmedReason) {
      toast.error("Please provide a reason before continuing.");
      return;
    }
    updateCandidateStatusMutation.mutate({
      id: candidateDialogState.candidate.id,
      isMatch: candidateDialogState.mode === "reinstate",
      reason: trimmedReason,
    });
  };

  const onSubmit = (data: SearchFormData) => {
    const city = data.city[0];
    const lat = city?.lat;
    const lng = city?.lng;

    const specialtyCodes = data.specialties ?? [];
    const professionalTypeSelections = data.professionalTypes ?? [];
    const professionalTypeIds = professionalTypeSelections.map(
      (type) => type.id,
    );
    const hasLocation = lat !== undefined && lng !== undefined;
    const hasStateLicenses = data.stateLicenses.length > 0;
    const stateLicenses = hasStateLicenses ? data.stateLicenses : undefined;
    const hasImlcLicenseValue =
      data.hasImlcLicense === "true"
        ? true
        : data.hasImlcLicense === "false"
          ? false
          : undefined;
    const employmentTypeIds =
      data.employmentTypeIds && data.employmentTypeIds.length > 0
        ? data.employmentTypeIds
        : undefined;

    const params = {
      lat,
      lng,
      specialtyCodes: specialtyCodes.length > 0 ? specialtyCodes : undefined,
      distanceMiles: parseInt(data.distance),
      employmentTypeIds,
      stateLicenses,
      hasImlcLicense: hasImlcLicenseValue,
      healthcareProfessionalTypeIds:
        professionalTypeIds.length > 0 ? professionalTypeIds : undefined,
    };

    const hasFilters =
      specialtyCodes.length > 0 ||
      hasLocation ||
      hasStateLicenses ||
      typeof hasImlcLicenseValue === "boolean" ||
      professionalTypeIds.length > 0;

    setSearchParams(hasFilters ? params : null);

    const nextSearch: Record<string, string> = {
      distance: data.distance,
    };

    if (lat && lng && city) {
      nextSearch.lat = String(lat);
      nextSearch.lng = String(lng);
      nextSearch.cityName = city.name;
    }

    if (specialtyCodes.length > 0) {
      nextSearch.specialties = specialtyCodes.join(",");
    }

    if (hasStateLicenses) {
      nextSearch.stateLicenses = data.stateLicenses.join(",");
    }

    if (data.hasImlcLicense !== "any") {
      nextSearch.hasImlcLicense = data.hasImlcLicense;
    }

    if (professionalTypeSelections.length > 0) {
      nextSearch.professionalTypes = JSON.stringify(professionalTypeSelections);
    }

    if (employmentTypeIds && employmentTypeIds.length > 0) {
      nextSearch.employmentTypeIds = employmentTypeIds.join(",");
    }

    navigate({
      search: nextSearch,
    });
  };

  const handleOpenChat = async (user: {
    id: string;
    firstName: string | null;
    lastName: string | null;
  }) => {
    setChatLoadingUserId(user.id);
    try {
      const result = await ensureChatMutation.mutateAsync({
        userId: user.id,
      });
      setChatDialogUser(user);
      setChatDialogThreadId(result.threadId);
      setIsChatDialogOpen(true);
    } catch (error) {
      // handled in mutation onError
    } finally {
      setChatLoadingUserId(null);
    }
  };

  const handleChatDialogChange = (open: boolean) => {
    if (!open) {
      setIsChatDialogOpen(false);
      setChatDialogThreadId(null);
      setChatDialogUser(null);
    } else {
      setIsChatDialogOpen(true);
    }
  };

  const providers = providersQuery.data ?? [];
  const providerIds = providers.map((provider) => provider.user_id);
  const providerIdsKey = providerIds.join("|");
  const providerStatusesQuery = useQuery({
    queryKey: candidateStatusesQueryKey(jobPostId, providerIdsKey),
    enabled: providerIds.length > 0,
    queryFn: async () =>
      trpcClient.jobPost.adminGetPotentialCandidateStatuses.query({
        jobPostId,
        userIds: providerIds,
      }),
  });
  const providerStatusesMap = useMemo(() => {
    const map = new Map<string, ProviderCandidateStatus>();
    providerStatusesQuery.data?.forEach((status) => {
      map.set(status.userId, status);
    });
    return map;
  }, [providerStatusesQuery.data]);
  const overviewItems = useMemo<OverviewItem[]>(() => {
    if (!job) {
      return [];
    }

    const items: OverviewItem[] = [];

    if (job.wageMinAmount || job.wageMaxAmount) {
      const min = job.wageMinAmount ? Number(job.wageMinAmount) : null;
      const max = job.wageMaxAmount ? Number(job.wageMaxAmount) : null;
      const formattedComp =
        min && max
          ? `${formatCurrency(min)} - ${formatCurrency(max)}`
          : min
            ? formatCurrency(min)
            : max
              ? formatCurrency(max)
              : null;

      if (formattedComp) {
        items.push({
          key: "compensation",
          label: "Compensation",
          value: (
            <div className="space-y-1">
              <p className="font-semibold text-gray-900">{formattedComp}</p>
              {job.wageFrequencyId && (
                <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                  / {job.wageFrequencyId}
                </p>
              )}
              {job.guaranteedBasePay && (
                <p className="text-xs text-gray-600">
                  Guaranteed base pay
                  {job.guaranteedBasePayDescription && (
                    <span className="ml-1 text-gray-500">
                      {job.guaranteedBasePayDescription}
                    </span>
                  )}
                </p>
              )}
            </div>
          ),
        });
      }
    }

    items.push({
      key: "organization",
      label: "Organization",
      value: (
        <div className="space-y-1">
          <p className="font-semibold text-gray-900">{organizationName}</p>
          {businessUnitName && (
            <p className="text-xs text-gray-600">
              Business unit: {businessUnitName}
            </p>
          )}
        </div>
      ),
    });

    items.push({
      key: "doctor-type",
      label: "Doctor Type",
      value:
        professionalTypes.length > 0 ? (
          <div className="flex flex-wrap gap-1.5">
            {professionalTypes.map((type) => (
              <Badge
                key={type.id ?? type.name}
                variant="outline"
                className="border-blue-200 bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700"
              >
                {type.name}
              </Badge>
            ))}
          </div>
        ) : (
          <p className="text-xs text-gray-500">Not specified</p>
        ),
    });

    items.push({
      key: "specialties",
      label: "Specialties",
      value:
        specialties.length > 0 ? (
          <div className="flex flex-wrap gap-1.5">
            {specialties.map((specialty) => (
              <Badge
                key={specialty.id ?? specialty.name}
                variant="outline"
                className="border-green-200 bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700"
              >
                {specialty.name}
              </Badge>
            ))}
          </div>
        ) : (
          <p className="text-xs text-gray-500">Not specified</p>
        ),
    });

    if (job.startDate) {
      items.push({
        key: "start-date",
        label: "Start Date",
        value: (
          <p className="text-sm text-gray-900">
            {format(new Date(job.startDate), "MMM d, yyyy")}
          </p>
        ),
      });
    }

    if (job.endDate) {
      items.push({
        key: "end-date",
        label: "End Date",
        value: (
          <p className="text-sm text-gray-900">
            {format(new Date(job.endDate), "MMM d, yyyy")}
          </p>
        ),
      });
    }

    items.push({
      key: "interested-candidates",
      label: "Interested Candidates",
      value: (
        <div className="space-y-1">
          <p className="text-lg font-semibold text-gray-900">
            {job.interestedCandidates?.length ?? 0}
          </p>
          <p className="text-xs uppercase tracking-wide text-gray-500">
            Active
          </p>
          <p className="text-xs text-gray-500">Added through this job post</p>
        </div>
      ),
    });

    items.push({
      key: "potential-candidates",
      label: "Potential Candidates",
      value: (
        <div className="space-y-1">
          <div className="flex flex-wrap items-baseline gap-2">
            <p className="text-lg font-semibold text-gray-900">
              {matchesCount}
            </p>
            <span className="text-xs uppercase tracking-wide text-gray-500">
              Matches
            </span>
          </div>
          <p className="text-xs text-gray-500">{rejectedCount} rejected</p>
          <Button
            variant="link"
            className="h-auto px-0 text-xs font-medium text-blue-600"
            onClick={scrollToCandidates}
          >
            View candidate list
          </Button>
        </div>
      ),
    });

    return items;
  }, [
    businessUnitName,
    job,
    matchesCount,
    organizationName,
    professionalTypes,
    rejectedCount,
    scrollToCandidates,
    specialties,
  ]);
  return (
    <div className="container mx-auto py-8 px-4 space-y-6">
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          onClick={() =>
            navigate({
              to: "/admin/job-posts",
              search: true,
            })
          }
          className="px-2"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to job posts
        </Button>
        <h1 className="text-2xl font-bold">Job Details</h1>
      </div>

      <Card className="p-6 space-y-6">
        {jobQuery.isLoading ? (
          <div className="flex items-center gap-2 text-gray-600">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading job details…
          </div>
        ) : job ? (
          <>
            <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
              <div>
                <p className="text-sm text-gray-500 uppercase tracking-wide">
                  Job
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  {job.title || "Untitled Job"}
                </p>
                <p className="text-gray-600">
                  {job.facilityName ||
                    job.facility?.name ||
                    "No facility specified"}
                </p>
                {job.city && job.stateCode ? (
                  <p className="flex items-center gap-2 text-gray-600">
                    <MapPin className="h-4 w-4 text-gray-500" />
                    <span>
                      {job.city}, {job.stateCode}
                    </span>
                  </p>
                ) : (
                  <p className="text-gray-600">Location not specified</p>
                )}
              </div>
              {getStatusBadge(job)}
            </div>

            {overviewItems.length > 0 && (
              <div className="border-t border-gray-200 pt-5">
                <dl className="grid gap-x-8 gap-y-4 sm:grid-cols-2 lg:grid-cols-3">
                  {overviewItems.map(({ key, label, value }) => (
                    <div key={key} className="space-y-1">
                      <dt className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                        {label}
                      </dt>
                      <dd className="text-sm leading-5 text-gray-900">
                        {value}
                      </dd>
                    </div>
                  ))}
                </dl>
              </div>
            )}

            <div className="flex flex-wrap gap-3 pt-2">
              <Button
                onClick={() => setPreviewJob(job as unknown as JobPost)}
                variant="outline"
                className="border-blue-300 text-blue-700 hover:bg-blue-50"
              >
                <Eye className="h-4 w-4 mr-2" />
                Preview
              </Button>
              {job.organizationBusinessUnit?.id && (
                <Button
                  onClick={() =>
                    navigate({
                      to: `/org/bu/${job.organizationBusinessUnit!.id}/jobs/post/edit/${job.id}`,
                    })
                  }
                  variant="outline"
                  className="border-purple-300 text-purple-700 hover:bg-purple-50"
                >
                  <Pencil className="h-4 w-4 mr-2" />
                  Edit Job Post
                </Button>
              )}
              {job.organizationBusinessUnit?.id && (
                <Button
                  onClick={() =>
                    navigate({
                      to: `/org/bu/${job.organizationBusinessUnit!.id}/jobs/post/dashboard/${job.id}`,
                    })
                  }
                  variant="outline"
                  className="border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                >
                  <LayoutDashboard className="h-4 w-4 mr-2" />
                  View Dashboard
                </Button>
              )}
              {!job.reviewedAt ? (
                <Button
                  onClick={() => setReviewDialogOpen(true)}
                  className="bg-green-600 hover:bg-green-700 border-green-700 text-white"
                  disabled={reviewMutation.isPending}
                >
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  Approve Job Post
                </Button>
              ) : (
                <Button
                  onClick={() => setUnapproveDialogOpen(true)}
                  variant="outline"
                  className="border-orange-300 text-orange-700 hover:bg-orange-50"
                  disabled={removeApprovalMutation.isPending}
                >
                  <StopCircle className="h-4 w-4 mr-2" />
                  Remove Approval
                </Button>
              )}
              {job.publishedAt && (
                <>
                  <Button
                    onClick={() =>
                      reevaluateCandidatesMutation.mutate({ id: job.id })
                    }
                    variant="outline"
                    className="border-blue-300 text-blue-700 hover:bg-blue-50"
                    disabled={reevaluateCandidatesMutation.isPending}
                  >
                    {reevaluateCandidatesMutation.isPending ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <RefreshCw className="h-4 w-4 mr-2" />
                    )}
                    Reevaluate Candidates
                  </Button>
                  <Button
                    onClick={() => setSqlDialogOpen(true)}
                    variant="outline"
                    className="border-gray-300 text-gray-700 hover:bg-gray-50"
                  >
                    <Database className="h-4 w-4 mr-2" />
                    View Matching SQL
                  </Button>
                </>
              )}
            </div>
          </>
        ) : (
          <p className="text-sm text-red-600">
            Unable to load job details. The job may have been removed.
          </p>
        )}
      </Card>

      {job && (
        <Card className="p-6 space-y-6">
          {(durationText ||
            job.scheduleDescription ||
            job.description ||
            (job.responsibilities?.length ?? 0) > 0 ||
            (job.requiredQualifications?.length ?? 0) > 0 ||
            (job.preferredQualifications?.length ?? 0) > 0) && (
            <div className="rounded-lg border border-gray-200">
              <button
                type="button"
                className="flex w-full items-center justify-between px-4 py-3 text-left"
                onClick={() => setDetailsExpanded((prev) => !prev)}
              >
                <span className="font-semibold text-gray-900">Job Content</span>
                <ChevronDown
                  className={`h-4 w-4 text-gray-500 transition-transform ${
                    detailsExpanded ? "rotate-180" : ""
                  }`}
                />
              </button>
              {detailsExpanded && (
                <div className="border-t border-gray-200 bg-gray-50 px-4 py-4 space-y-5 text-sm text-gray-700">
                  {durationText && (
                    <div>
                      <h3 className="font-semibold text-gray-900">
                        Contract Duration
                      </h3>
                      <p className="text-gray-700 mt-1">{durationText}</p>
                    </div>
                  )}
                  {job.scheduleDescription && (
                    <div>
                      <h3 className="font-semibold flex items-center gap-2 text-gray-900">
                        <Clock className="h-4 w-4" />
                        Schedule
                      </h3>
                      <div className="prose prose-sm max-w-none text-gray-700 mt-2">
                        <RenderHtml html={job.scheduleDescription} />
                      </div>
                    </div>
                  )}
                  {job.description && (
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-2">
                        Job Description
                      </h3>
                      <div className="prose prose-sm max-w-none text-gray-700">
                        <RenderHtml html={job.description} />
                      </div>
                    </div>
                  )}
                  {job.responsibilities && job.responsibilities.length > 0 && (
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-2">
                        Responsibilities
                      </h3>
                      <ul className="space-y-2 text-gray-700">
                        {job.responsibilities.map(
                          (responsibility: string, index: number) => (
                            <li
                              key={`${responsibility}-${index}`}
                              className="flex gap-2"
                            >
                              <span className="text-blue-500">•</span>
                              <span>{responsibility}</span>
                            </li>
                          ),
                        )}
                      </ul>
                    </div>
                  )}
                  {job.requiredQualifications &&
                    job.requiredQualifications.length > 0 && (
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-2">
                          Required Qualifications
                        </h3>
                        <ul className="space-y-2 text-gray-700">
                          {job.requiredQualifications.map(
                            (qualification: string, index: number) => (
                              <li
                                key={`${qualification}-${index}`}
                                className="flex gap-2"
                              >
                                <span className="text-red-500">•</span>
                                <span>{qualification}</span>
                              </li>
                            ),
                          )}
                        </ul>
                      </div>
                    )}
                  {job.preferredQualifications &&
                    job.preferredQualifications.length > 0 && (
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-2">
                          Preferred Qualifications
                        </h3>
                        <ul className="space-y-2 text-gray-700">
                          {job.preferredQualifications.map(
                            (qualification: string, index: number) => (
                              <li
                                key={`${qualification}-${index}`}
                                className="flex gap-2"
                              >
                                <span className="text-green-500">•</span>
                                <span>{qualification}</span>
                              </li>
                            ),
                          )}
                        </ul>
                      </div>
                    )}
                </div>
              )}
            </div>
          )}

          {job.dailyPatientVolume && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-indigo-50 p-4 rounded-lg">
                <p className="text-xs uppercase text-indigo-900">
                  Daily Patient Volume
                </p>
                <p className="text-indigo-800 font-semibold">
                  {job.dailyPatientVolume} patients/day
                </p>
              </div>
            </div>
          )}

          {job.interestedCandidates && job.interestedCandidates.length > 0 && (
            <div className="rounded-lg border border-blue-200">
              <button
                type="button"
                className="flex w-full items-center justify-between px-4 py-3 text-left"
                onClick={() => setInterestedExpanded((prev) => !prev)}
              >
                <span className="font-semibold text-gray-900 flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  Interested Candidates ({job.interestedCandidates.length})
                </span>
                <ChevronDown
                  className={`h-4 w-4 text-gray-500 transition-transform ${
                    interestedExpanded ? "rotate-180" : ""
                  }`}
                />
              </button>
              {interestedExpanded && (
                <div className="border-t border-blue-100 bg-blue-50 px-4 py-3 space-y-2">
                  {job.interestedCandidates.map(
                    (candidate: InterestedCandidate, index: number) => (
                      <div
                        key={`${candidate.id ?? index}`}
                        className="flex items-center justify-between bg-white p-3 rounded-lg border border-blue-200"
                      >
                        <span className="font-medium text-gray-900">
                          {candidate.firstName} {candidate.lastName}
                        </span>
                        {candidate.interestedAt && (
                          <span className="text-sm text-gray-600">
                            {format(
                              new Date(candidate.interestedAt),
                              "MMM d, yyyy",
                            )}
                          </span>
                        )}
                      </div>
                    ),
                  )}
                </div>
              )}
            </div>
          )}

          <div className="bg-gray-50 p-4 rounded-lg space-y-2">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Timeline
            </h3>
            {job.publishedAt ? (
              <div className="flex items-center gap-2 text-sm text-gray-700">
                <Calendar className="h-4 w-4" />
                <span>
                  Published{" "}
                  {format(new Date(job.publishedAt), "MMM d, yyyy 'at' h:mm a")}
                </span>
              </div>
            ) : (
              <div className="flex items-center gap-2 text-sm text-gray-700">
                <FileSignature className="h-4 w-4" />
                <span>Draft: Not yet published</span>
              </div>
            )}
            {job.reviewedAt && (
              <div className="flex items-center gap-2 text-sm text-gray-700">
                <Eye className="h-4 w-4" />
                <span>
                  Reviewed{" "}
                  {format(new Date(job.reviewedAt), "MMM d, yyyy 'at' h:mm a")}
                </span>
              </div>
            )}
            {job.createdBy && (
              <div className="flex items-center gap-2 text-sm text-gray-700">
                <User className="h-4 w-4" />
                <span>
                  Created by {job.createdBy.firstName ?? job.createdBy.email}{" "}
                  {job.createdBy.lastName}
                </span>
              </div>
            )}
            <div className="flex items-center gap-2 text-sm text-gray-700">
              <Building2 className="h-4 w-4" />
              <span>{organizationName}</span>
            </div>
          </div>
        </Card>
      )}

      <div ref={candidateSectionRef}>
        <Card className="space-y-4 p-6">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <h2 className="text-xl font-semibold">
                Current Potential Candidates
              </h2>
              <p className="text-sm text-gray-500">
                {matchesCount} total candidate
                {matchesCount === 1 ? "" : "s"}
              </p>
            </div>
            <Tabs
              value={candidateStatusFilter}
              onValueChange={(value) =>
                setCandidateStatusFilter(value as CandidateStatusFilter)
              }
              className="md:justify-end"
            >
              <TabsList className="h-12 p-1.5 bg-gray-100 border border-gray-200">
                <TabsTrigger
                  value="matches"
                  className="px-6 py-2.5 text-base font-semibold data-[state=active]:bg-white data-[state=active]:shadow-sm"
                >
                  Matches ({matchesCount})
                </TabsTrigger>
                <TabsTrigger
                  value="rejected"
                  className="px-6 py-2.5 text-base font-semibold data-[state=active]:bg-white data-[state=active]:shadow-sm"
                >
                  Rejected ({rejectedCount})
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
          {candidatesQuery.isLoading ? (
            <div className="flex items-center gap-2 text-gray-600 py-6">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading candidates…
            </div>
          ) : candidates.length === 0 ? (
            <div className="text-gray-600">
              {isViewingRejected
                ? "No rejected candidates yet."
                : "No matched candidates yet. Use the search below to add providers to this list."}
            </div>
          ) : (
            <div className="space-y-3">
              {candidates.map((candidate) => {
                const stateLicenses = Array.isArray(candidate.stateLicenses)
                  ? candidate.stateLicenses.filter(isStateMedicalLicense)
                  : [];
                const hasStateLicenses = stateLicenses.length > 0;
                const jobDecision = candidate.jobPostDecision;
                const lastChatAt = candidate.chatMessage?.createdAt
                  ? new Date(candidate.chatMessage?.createdAt)
                  : null;
                const lastChatRelativeTime = lastChatAt
                  ? formatDistanceToNow(lastChatAt, { addSuffix: true })
                  : null;
                const showAiReason =
                  candidate.isMatchChangedBy === "ai" &&
                  Boolean(candidate.aiEvaluationReason);
                const providerTypeName =
                  candidate.providerType?.name?.trim() || null;
                const specialties = Array.isArray(candidate.specialties)
                  ? candidate.specialties.filter(
                      (specialty: CandidateSpecialty) =>
                        specialty &&
                        typeof specialty.name === "string" &&
                        specialty.name.length > 0,
                    )
                  : [];

                return (
                  <Card key={candidate.id} className="p-4 space-y-4">
                    <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                      <div className="space-y-3">
                        <div className="flex flex-wrap items-center gap-2">
                          <h3 className="text-lg font-semibold text-gray-900">
                            {candidate.firstName} {candidate.lastName}
                          </h3>
                          <Badge
                            variant={
                              candidate.isMatch ? "secondary" : "destructive"
                            }
                            size="light"
                          >
                            {candidate.isMatch ? "Matched" : "Rejected"}
                          </Badge>
                          {showAiReason && (
                            <Popover>
                              <PopoverTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-gray-500"
                                  aria-label="View AI reasoning"
                                >
                                  <Info className="h-4 w-4" />
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="max-w-sm rounded-md border border-gray-200 bg-white p-4 text-sm text-gray-700 shadow-lg">
                                <p className="mb-1 font-semibold">
                                  AI evaluation insight
                                </p>
                                <p>{candidate.aiEvaluationReason}</p>
                              </PopoverContent>
                            </Popover>
                          )}
                        </div>
                        {!candidate.isMatch && showAiReason && (
                          <div className="max-w-2xl rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                            <span className="font-semibold">
                              Why they weren’t added:
                            </span>{" "}
                            {candidate.aiEvaluationReason}
                          </div>
                        )}
                        <div className="text-sm text-gray-800">
                          {providerTypeName ?? "Provider type not specified"}
                        </div>
                        <div className="text-sm text-gray-800">
                          {specialties.length > 0 ? (
                            <span className="inline-flex flex-wrap gap-1">
                              {specialties.map((specialty) => (
                                <span
                                  key={`${candidate.id}-${specialty.id ?? specialty.code ?? specialty.name}`}
                                  className="inline-flex items-center rounded border border-gray-200 bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700"
                                >
                                  {specialty.name}
                                </span>
                              ))}
                            </span>
                          ) : (
                            <span className="text-gray-400">
                              Specialties not specified
                            </span>
                          )}
                        </div>
                        {candidate.userEvaluationReason && (
                          <p className="text-sm text-gray-600">
                            <span className="font-medium text-gray-700">
                              Internal note:
                            </span>{" "}
                            {candidate.userEvaluationReason}
                          </p>
                        )}
                        {jobDecision && (
                          <div className="flex flex-wrap items-center gap-2 text-sm text-gray-700">
                            <Badge
                              variant={
                                jobDecision.interested ? "secondary" : "warning"
                              }
                              size="light"
                            >
                              {jobDecision.interested
                                ? "Provider Interested"
                                : "Provider Declined"}
                            </Badge>
                            {jobDecision.createdAt && (
                              <span className="text-gray-500">
                                {formatDistanceToNow(
                                  new Date(jobDecision.createdAt),
                                  { addSuffix: true },
                                )}
                              </span>
                            )}
                          </div>
                        )}
                        <div className="text-sm text-gray-600">
                          {hasStateLicenses ? (
                            <span className="inline-flex flex-wrap gap-1">
                              {stateLicenses.map((license, index) => (
                                <span
                                  key={`${candidate.id}-${license.state}-${index}`}
                                  className="inline-flex items-center rounded border border-gray-200 bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700"
                                >
                                  {license.state}
                                </span>
                              ))}
                            </span>
                          ) : (
                            <span className="text-gray-400">
                              No state licenses
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-gray-600">
                          <span className="flex items-center gap-1 text-gray-500">
                            <Clock className="h-3.5 w-3.5" />
                            Last chat
                          </span>
                          {lastChatRelativeTime ? (
                            <div className="mt-1 space-y-1 text-gray-700">
                              <p>{getLastChatPreview(candidate)}</p>
                              <span className="text-gray-500 text-sm">
                                {lastChatRelativeTime}
                              </span>
                            </div>
                          ) : (
                            <p className="text-gray-400">No chat history yet</p>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Button
                          variant="secondary"
                          onClick={() =>
                            setProfileModalUserId(candidate.userId)
                          }
                        >
                          View Profile
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => setUserDetailsUserId(candidate.userId)}
                        >
                          User Details
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() =>
                            handleOpenChat({
                              id: candidate.userId,
                              firstName: candidate.firstName,
                              lastName: candidate.lastName,
                            })
                          }
                          disabled={chatLoadingUserId === candidate.userId}
                        >
                          {chatLoadingUserId === candidate.userId ? (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          ) : (
                            <MessageCircle className="mr-2 h-4 w-4" />
                          )}
                          Chat
                        </Button>
                        <Button
                          variant={
                            isViewingRejected ? "default" : "destructive"
                          }
                          onClick={() => {
                            setCandidateDialogState({
                              candidate,
                              mode: isViewingRejected ? "reinstate" : "reject",
                            });
                            setCandidateReason(
                              candidate.userEvaluationReason ?? "",
                            );
                          }}
                        >
                          {updateCandidateStatusMutation.isPending &&
                          candidateDialogState?.candidate.id ===
                            candidate.id ? (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          ) : isViewingRejected ? (
                            <UserPlus className="mr-2 h-4 w-4" />
                          ) : (
                            <UserMinus className="mr-2 h-4 w-4" />
                          )}
                          {isViewingRejected
                            ? "Add to potential candidates"
                            : "Reject"}
                        </Button>
                      </div>
                    </div>
                    {jobDecision?.description && (
                      <div className="rounded-md bg-gray-50 p-3 text-sm text-gray-700">
                        <span className="font-medium">Provider note:</span>{" "}
                        {jobDecision.description}
                      </div>
                    )}
                    {jobDecision?.organizationNotes && (
                      <div className="rounded-md border border-gray-100 bg-slate-50 p-3 text-sm text-gray-700">
                        <span className="font-medium">Internal notes:</span>{" "}
                        {jobDecision.organizationNotes}
                      </div>
                    )}
                  </Card>
                );
              })}
            </div>
          )}
        </Card>
      </div>

      <Card className="p-6">
        <h2 className="text-xl font-semibold mb-4">
          Find More Potential Candidates
        </h2>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
            <div className="md:col-span-4">
              <label className="block text-sm font-medium mb-2">
                Professional Type
              </label>
              <HealthcareProfessionalTypeSelect
                isMulti
                value={watch("professionalTypes")}
                onChange={(value) =>
                  setValue(
                    "professionalTypes",
                    (value as { id: string; name: string }[] | undefined) ?? [],
                    {
                      shouldValidate: true,
                    },
                  )
                }
                placeholder="Select professional type"
              />
            </div>

            <div className="md:col-span-4">
              <label className="block text-sm font-medium mb-2">
                Specialties
              </label>
              <SpecialtiesSelect
                isMulti
                value={watch("specialties")}
                onChange={(value) =>
                  setValue("specialties", (value as string[] | null) ?? [], {
                    shouldValidate: true,
                  })
                }
              />
              {errors.specialties && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.specialties.message}
                </p>
              )}
            </div>

            <div className="md:col-span-4">
              <label className="block text-sm font-medium mb-2">City</label>
              <CitySelect
                value={watch("city")}
                onChange={(value: CityOption[]) =>
                  setValue("city", value, { shouldValidate: true })
                }
              />
              {errors.city && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.city.message}
                </p>
              )}
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium mb-2">
                Employment Type
              </label>
              <EmploymentTypeSelect
                value={watch("employmentTypeIds")}
                onChange={(value) => setValue("employmentTypeIds", value)}
                placeholder="Any type"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium mb-2">Distance</label>
              <Select
                value={watch("distance")}
                onValueChange={(value) =>
                  setValue("distance", value, { shouldValidate: true })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select distance" />
                </SelectTrigger>
                <SelectContent>
                  {distanceOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.distance && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.distance.message}
                </p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">
                State Licenses
              </label>
              <USStateSelect
                multiple={true}
                value={watch("stateLicenses")}
                onChange={(value) =>
                  setValue("stateLicenses", value as string[], {
                    shouldValidate: true,
                  })
                }
                placeholder="Select states"
              />
              {errors.stateLicenses && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.stateLicenses.message}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                IMLC License
              </label>
              <Select
                value={watch("hasImlcLicense")}
                onValueChange={(value) => setValue("hasImlcLicense", value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Any" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any</SelectItem>
                  <SelectItem value="true">Yes</SelectItem>
                  <SelectItem value="false">No</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <Button type="submit" className="w-full py-8">
            Search Providers
          </Button>
        </form>

        <div className="mt-8">
          {providersQuery.isLoading ? (
            <div className="flex items-center gap-2 justify-center text-gray-600 py-8">
              <Loader2 className="h-4 w-4 animate-spin" />
              Searching providers…
            </div>
          ) : !searchParams ? (
            <div className="text-center py-8">
              <h3 className="text-lg font-semibold text-gray-700 mb-2">
                Enter Search Parameters
              </h3>
              <p className="text-gray-500">
                Add filters above to find providers who may be a good fit for
                this role.
              </p>
            </div>
          ) : providers.length > 0 ? (
            <div className="space-y-4">
              {providers.map((provider) => {
                const candidateStatus = providerStatusesMap.get(
                  provider.user_id,
                );
                const isAlreadyCandidate = Boolean(
                  candidateStatus && candidateStatus.isMatch !== null,
                );
                const isRejectedCandidate = candidateStatus?.isMatch === false;
                const failedQueryConditions =
                  candidateStatus?.matchingEvaluation?.conditions.filter(
                    (condition) => !condition.matched,
                  ) ?? [];
                const matchesNonAiFilters =
                  candidateStatus?.matchesInitialQuery === true;
                const filterExplanation = matchesNonAiFilters
                  ? "This provider currently passes every non-AI matching filter."
                  : candidateStatus?.matchesInitialQuery === null
                    ? "The non-AI matching filters could not be evaluated for this provider."
                    : null;
                const providerWithLicenses = provider as {
                  state_medical_licenses?: unknown;
                };
                const stateMedicalLicenses = Array.isArray(
                  providerWithLicenses.state_medical_licenses,
                )
                  ? providerWithLicenses.state_medical_licenses.filter(
                      isStateMedicalLicense,
                    )
                  : [];
                const hasStateMedicalLicenses = stateMedicalLicenses.length > 0;
                return (
                  <Card key={provider.user_id} className="p-4">
                    <div className="flex flex-col md:flex-row justify-between items-start gap-4">
                      <div>
                        <h3 className="text-lg font-semibold">
                          {provider.first_name} {provider.last_name}
                        </h3>
                        <div className="mt-1 flex flex-wrap items-center gap-2 text-sm">
                          {isAlreadyCandidate ? (
                            <Badge
                              variant={
                                isRejectedCandidate
                                  ? "destructive"
                                  : "secondary"
                              }
                              size="light"
                            >
                              {isRejectedCandidate ? "Rejected" : "Added"}
                            </Badge>
                          ) : (
                            <Badge variant="outline" size="light">
                              Not Added
                            </Badge>
                          )}
                        </div>
                        {(filterExplanation ||
                          failedQueryConditions.length > 0) && (
                          <div
                            className={`mt-2 max-w-2xl rounded-md border px-3 py-2 text-sm ${
                              matchesNonAiFilters
                                ? "border-green-200 bg-green-50 text-green-900"
                                : "border-amber-200 bg-amber-50 text-amber-900"
                            }`}
                          >
                            <span className="font-semibold">
                              Non-AI filter result:
                            </span>{" "}
                            {filterExplanation && (
                              <span>{filterExplanation}</span>
                            )}
                            {failedQueryConditions.length > 0 && (
                              <ul className="mt-1 list-disc space-y-1 pl-5">
                                {failedQueryConditions.map((condition) => (
                                  <li key={condition.key}>
                                    {condition.explanation}
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        )}
                        <p>{provider.specialty_name}</p>
                        {provider.organizations &&
                          provider.organizations.length > 0 && (
                            <p className="text-sm">
                              <span className="text-gray-500">Orgs:</span>{" "}
                              {provider.organizations.map((org, i, arr) => (
                                <span key={i}>
                                  {org}
                                  {i < arr.length - 1 && " • "}
                                </span>
                              ))}
                            </p>
                          )}
                        <p className="text-sm">
                          <span className="text-gray-500">
                            Preferred locations:
                          </span>{" "}
                          {provider.locations
                            .filter((l) => l.name !== "ANY_CITY")
                            .map((l, i, arr) => (
                              <span key={l.name}>
                                {l.name}
                                {i < arr.length - 1 && " • "}
                              </span>
                            ))}
                          {provider.locations.some(
                            (l) => l.name === "ANY_CITY",
                          ) && (
                            <span className="text-gray-500">
                              {" "}
                              (open to other cities)
                            </span>
                          )}
                        </p>
                        <div className="flex flex-col gap-1">
                          <p className="text-sm">
                            <span className="text-gray-500">
                              Employment types:
                            </span>{" "}
                            <Popover>
                              <PopoverTrigger>
                                <span className="font-bold underline decoration-dotted cursor-help">
                                  {provider.employment_types.length === 0
                                    ? "Any Type"
                                    : provider.employment_types.join(" • ")}
                                </span>
                              </PopoverTrigger>
                              <PopoverContent className="w-64 rounded-md bg-white p-4 shadow-md border border-gray-200">
                                <div className="flex gap-2 items-center">
                                  <p className="text-sm text-gray-600">
                                    This provider's availability preference may
                                    change. Please confirm during outreach.
                                  </p>
                                </div>
                              </PopoverContent>
                            </Popover>
                          </p>
                          <p className="text-sm">
                            <span className="text-gray-500">
                              Practice settings:
                            </span>{" "}
                            {provider.practice_settings.length === 0
                              ? "Any Setting"
                              : provider.practice_settings.join(" • ")}
                          </p>
                          {provider.availability_description && (
                            <p className="text-sm">
                              <span className="text-gray-500">
                                Availability:
                              </span>{" "}
                              {provider.availability_description}
                            </p>
                          )}
                          <p className="text-sm">
                            <span className="text-gray-500">Min Wage:</span>{" "}
                            {!provider.minimum_rate ||
                            provider.minimum_rate === 0
                              ? "No minimum"
                              : `$${provider.minimum_rate}/hr`}
                          </p>
                          <p className="text-sm">
                            <span className="text-gray-500">IMLC License:</span>{" "}
                            <span className="font-semibold">
                              {provider.has_imlc_license ? "Yes" : "No"}
                            </span>
                          </p>
                          <div className="text-sm">
                            <span className="text-gray-500">
                              State Medical Licenses:
                            </span>{" "}
                            {hasStateMedicalLicenses ? (
                              <span className="inline-flex flex-wrap gap-1 ml-1">
                                {stateMedicalLicenses.map(
                                  (
                                    license: {
                                      state: string;
                                      license_number?: string | null;
                                      expiration_date?: string | null;
                                    },
                                    index: number,
                                  ) => (
                                    <span
                                      key={`${provider.user_id}-${license.state}-${index}`}
                                      className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200"
                                    >
                                      {license.state}
                                    </span>
                                  ),
                                )}
                              </span>
                            ) : (
                              <span className="text-gray-400">
                                No state licenses
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-col md:flex-row gap-2 w-full md:w-auto">
                        <Button
                          variant="secondary"
                          className="w-full md:w-auto"
                          onClick={() =>
                            setProfileModalUserId(provider.user_id)
                          }
                        >
                          View Provider Profile
                        </Button>
                        <Button
                          variant="outline"
                          className="w-full md:w-auto"
                          onClick={() =>
                            handleOpenChat({
                              id: provider.user_id,
                              firstName: provider.first_name,
                              lastName: provider.last_name,
                            })
                          }
                          disabled={chatLoadingUserId === provider.user_id}
                        >
                          {chatLoadingUserId === provider.user_id ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          ) : (
                            <MessageCircle className="h-4 w-4 mr-2" />
                          )}
                          Chat
                        </Button>
                        <Button
                          className="w-full md:w-auto"
                          onClick={() =>
                            addMutation.mutate({
                              jobPostId,
                              userId: provider.user_id,
                            })
                          }
                          disabled={addMutation.isPending || isAlreadyCandidate}
                        >
                          {addMutation.isPending ? (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          ) : (
                            <UserPlus className="h-4 w-4 mr-2" />
                          )}
                          {isAlreadyCandidate
                            ? isRejectedCandidate
                              ? "Marked as Rejected"
                              : "Already Added"
                            : "Add to Potential Candidates"}
                        </Button>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8">
              <h3 className="text-lg font-semibold text-gray-700 mb-2">
                No providers found
              </h3>
              <p className="text-gray-500 mb-4">
                Try expanding your filters to find more providers.
              </p>
              <ul className="text-gray-600 space-y-2">
                <li>• Expand your search radius</li>
                <li>• Try different specialties</li>
                <li>• Search in nearby cities</li>
              </ul>
            </div>
          )}
        </div>
      </Card>

      <Dialog open={reviewDialogOpen} onOpenChange={setReviewDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Approve Job Post</DialogTitle>
            <DialogDescription>
              Approve “{job?.title || "Untitled Job"}” to make it visible to
              providers.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setReviewDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleApproveJob}
              disabled={reviewMutation.isPending}
              className="bg-green-600 hover:bg-green-700"
            >
              {reviewMutation.isPending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle2 className="h-4 w-4 mr-2" />
              )}
              Approve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={unapproveDialogOpen} onOpenChange={setUnapproveDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove Approval</DialogTitle>
            <DialogDescription>
              This will move “{job?.title || "Untitled Job"}” back to pending
              review.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setUnapproveDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleRemoveApproval}
              disabled={removeApprovalMutation.isPending}
              className="bg-orange-600 hover:bg-orange-700"
            >
              {removeApprovalMutation.isPending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Undo2 className="h-4 w-4 mr-2" />
              )}
              Remove Approval
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <SqlDialog
        open={sqlDialogOpen}
        onOpenChange={setSqlDialogOpen}
        jobPostId={jobPostId}
      />

      <Dialog
        open={!!previewJob}
        onOpenChange={(open) => {
          if (!open) {
            setPreviewJob(null);
          }
        }}
      >
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Provider Preview</DialogTitle>
            <DialogDescription>
              This is how the job appears to providers.
            </DialogDescription>
          </DialogHeader>
          {previewJob && (
            <div className="mt-4">
              <JobCard
                job={previewJob}
                onInterestChange={mockOnInterestChange}
              />
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setPreviewJob(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(candidateDialogState)}
        onOpenChange={(open) => {
          if (!open) {
            closeCandidateDialog();
          }
        }}
      >
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {isReinstatingCandidate
                ? `Add ${candidateDialogName || "this provider"} back?`
                : `Reject ${candidateDialogName || "this provider"}?`}
            </DialogTitle>
            <DialogDescription>
              {isReinstatingCandidate
                ? "Update the note and mark this provider as a match again."
                : "Add an internal note explaining why this provider isn’t a fit. They’ll stay on the list as rejected."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              {isReinstatingCandidate ? "Internal note" : "Rejection reason"}
            </label>
            <Textarea
              value={candidateReason}
              onChange={(event) => setCandidateReason(event.target.value)}
              placeholder="Share context for your team..."
              maxLength={1000}
              className="min-h-[120px]"
            />
            <div className="text-xs text-gray-500 text-right">
              {candidateReason.length}/1000
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={closeCandidateDialog}
              disabled={updateCandidateStatusMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant={isReinstatingCandidate ? "default" : "destructive"}
              onClick={handleConfirmCandidateDialog}
              disabled={
                updateCandidateStatusMutation.isPending ||
                !candidateReason.trim()
              }
            >
              {updateCandidateStatusMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : isReinstatingCandidate ? (
                "Add to potential candidates"
              ) : (
                "Reject candidate"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {chatDialogUser && (
        <PotentialCandidateChatDialog
          open={isChatDialogOpen}
          onOpenChange={handleChatDialogChange}
          user={chatDialogUser}
          threadId={chatDialogThreadId}
        />
      )}
      {profileModalUserId && (
        <ProfileModal
          userId={profileModalUserId}
          onClose={() => setProfileModalUserId(null)}
        />
      )}
      {userDetailsUserId && (
        <Dialog
          open={!!userDetailsUserId}
          onOpenChange={() => setUserDetailsUserId(null)}
        >
          <DialogContent className="max-w-5xl w-full">
            <DialogTitle className="sr-only">User Details</DialogTitle>
            <AdminUserDetails
              userId={userDetailsUserId}
              context="dialog"
              onClose={() => setUserDetailsUserId(null)}
            />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

const SqlDialog = ({
  open,
  onOpenChange,
  jobPostId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  jobPostId: string;
}) => {
  const { data, isLoading, error } = useQuery(
    trpc.jobPost.adminGetMatchingSql.queryOptions(
      { jobPostId },
      { enabled: open },
    ),
  );

  const handleCopy = () => {
    if (data?.sql) {
      navigator.clipboard.writeText(data.sql);
      toast.success("SQL copied to clipboard");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Provider Matching SQL Query</DialogTitle>
          <DialogDescription>
            This is the SQL query used to find matching providers for this job
            post. You can copy and run it in the database to debug matching
            issues.
          </DialogDescription>
        </DialogHeader>
        <div className="flex-1 overflow-auto">
          {isLoading ? (
            <div className="flex items-center gap-2 text-gray-600 py-8 justify-center">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading SQL query…
            </div>
          ) : error ? (
            <div className="text-red-600 py-4">
              Error loading SQL: {error.message}
            </div>
          ) : (
            <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-auto text-xs font-mono whitespace-pre-wrap">
              {data?.sql}
            </pre>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          <Button onClick={handleCopy} disabled={!data?.sql}>
            Copy SQL
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const PotentialCandidateChatDialog = ({
  open,
  onOpenChange,
  user,
  threadId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: { id: string; firstName: string | null; lastName: string | null };
  threadId: string | null;
}) => {
  const queryClient = useQueryClient();
  const {
    data: messages,
    isLoading,
    error,
    refetch,
  } = useQuery(
    trpc.chat.adminGetChatThreadMessages.queryOptions(
      { threadId: threadId ?? "", includeDeletedAttachments: false },
      {
        enabled: open && Boolean(threadId),
        staleTime: 0,
        refetchInterval: () => 5_000 + Math.random() * 5_000,
      },
    ),
  );

  const sendMessageMutation = useMutation(
    trpc.chat.sendMessage.mutationOptions({
      onSuccess: async (_, variables) => {
        await queryClient.invalidateQueries({
          queryKey: trpc.chat.adminGetChatThreadMessages.queryKey({
            threadId: variables.threadId,
            includeDeletedAttachments: false,
          }),
        });
      },
      onError: () => {
        toast.error("Unable to send message. Please try again.");
      },
    }),
  );

  const typedMessages = useMemo<ChatThreadMessage[] | undefined>(() => {
    if (!messages) {
      return undefined;
    }
    return messages.map(adaptAdminMessageToUserMessage);
  }, [messages]);

  const handleSendMessage = async (
    message: string,
    attachmentIds?: string[],
  ) => {
    if (!threadId) {
      toast.error("Chat is not ready yet. Please try again.");
      return;
    }
    await sendMessageMutation.mutateAsync({
      threadId,
      message,
      attachmentIds,
    });
  };

  if (!user) {
    return null;
  }

  const displayName =
    [user.firstName, user.lastName]
      .filter((part) => part && part.length > 0)
      .join(" ")
      .trim() || "Provider";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg md:max-w-xl lg:max-w-2xl h-[85vh] max-h-dvh w-lg flex flex-col p-0 shadow-2xl rounded-lg">
        <DialogHeader className="border-b p-4">
          <DialogTitle className="text-lg font-medium">
            {displayName}
          </DialogTitle>
          <DialogDescription>
            Chat directly with this provider about the opportunity.
          </DialogDescription>
        </DialogHeader>
        {!threadId || isLoading ? (
          <div className="flex-1 flex items-center justify-center p-8">
            <div className="flex flex-col items-center text-center">
              <Loader2 className="h-8 w-8 text-blue-600 animate-spin mb-3" />
              <p className="text-gray-600">Loading conversation…</p>
            </div>
          </div>
        ) : error ? (
          <div className="flex-1 flex items-center justify-center p-8">
            <Alert variant="destructive" className="max-w-md">
              <AlertDescription>
                There was an error loading this conversation. Please try again.
              </AlertDescription>
              <Button
                variant="outline"
                size="sm"
                className="mt-3"
                onClick={() => refetch()}
              >
                Retry
              </Button>
            </Alert>
          </div>
        ) : (
          <ChatThreadBody
            messages={typedMessages ?? []}
            onSendMessage={handleSendMessage}
            readOnly={false}
            threadId={threadId ?? ""}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

export const Route = createFileRoute("/_admin/admin/job-posts/$jobPostId/")({
  component: PotentialCandidatesPage,
  validateSearch: z
    .object({
      lat: z.string().optional().default(""),
      lng: z.string().optional().default(""),
      cityName: z.string().optional().default(""),
      specialty: z.string().optional().default(""),
      specialties: z.string().optional().default(""),
      distance: z.string().optional().default(""),
      employmentTypeIds: z.string().optional().default(""),
      stateLicenses: z.string().optional().default(""),
      hasImlcLicense: z.string().optional().default(""),
      professionalTypeId: z.string().optional().default(""),
      professionalTypeName: z.string().optional().default(""),
      professionalTypes: z.string().optional().default(""),
    })
    .optional()
    .default({}),
});
